import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    transactionContract,
    transactionFieldReviewContract,
} from "@common/contracts/transaction.contract";
import {
    BulkFieldReviewRequest,
    BulkUpdateTransactionsRequestInput,
    TransactionFilters,
    TransactionSort,
    bulkUpdateTransactionsResponseSchema,
    getTransactionsResponseSchema,
    getTransactionsResponseWithCountSchema,
    getTransactionsSnapshotResponseSchema,
    GetTransactionsResponse,
    GetTransactionsResponseWithCount,
    GetTransactionsSnapshotResponse,
    TransactionsStatistics,
} from "@common/schemas/transaction.schema";
import {
    SuggestedAccrualMatchBillsResponse,
    SuggestedAccrualMatchBillsQuery,
    SuggestedAccrualMatchInvoicesResponse,
    SuggestedAccrualMatchInvoicesQuery,
    suggestedAccrualMatchBillsResponseSchema,
    suggestedAccrualMatchInvoicesResponseSchema,
} from "@common/schemas/domains/accrualReview/suggested-accrual-match-response.schema";
import { MATCHED_INVOICE_OR_BILL_UNSPLIT_MESSAGE } from "@common/helpers/transactions";
import { Transaction } from "@common/types/transaction";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { TransactionFieldReviewDto } from "@common/schemas/transactionFieldReview.schema";
import { isAxiosError } from "axios";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { removeJournalEntryQueries } from "@components/accounting/journalEntryDrawer/journalEntryQuery";

const transactionContractClient = getBackendAPIClient(transactionContract);
const fieldReviewClient = getBackendAPIClient(transactionFieldReviewContract);

interface GetTransactionsParams {
    sort?: TransactionSort;
    filters?: TransactionFilters;
    page?: number;
    limit?: number;
    workspaceId: string;
    skipCount?: boolean;
    includeMatchDetails?: boolean;
    includeAccrualMatchDetails?: boolean;
    includeAccrualMatchSuggestions?: boolean;
}

export async function getTransactions(
    data: Omit<GetTransactionsParams, "skipCount"> & { skipCount?: false },
): Promise<GetTransactionsResponseWithCount>;
export async function getTransactions(
    data: GetTransactionsParams & { skipCount: true },
): Promise<GetTransactionsResponse>;
export async function getTransactions(
    data: GetTransactionsParams,
): Promise<GetTransactionsResponse>;
export async function getTransactions(
    data: GetTransactionsParams,
): Promise<GetTransactionsResponse> {
    const {
        sort,
        filters,
        page,
        limit,
        workspaceId,
        skipCount,
        includeMatchDetails,
        includeAccrualMatchDetails,
        includeAccrualMatchSuggestions,
    } = data;

    const response = await transactionContractClient.queryTransactions({
        body: {
            page,
            limit,
            sort: sort
                ? {
                      field: sort.field,
                      order: sort.order,
                  }
                : undefined,
            filters,
            workspaceId,
            skipCount,
            includeMatchDetails,
            includeAccrualMatchDetails,
            includeAccrualMatchSuggestions,
        },
    });

    if (response.status !== 200) {
        throw new Error("Failed to fetch transactions");
    }

    return getTransactionsResponseSchema.parse(response.body);
}

/**
 * Every matching transaction id in list order (capped server-side), plus the
 * rows of the first `limit` ids.
 */
export async function getTransactionsSnapshot({
    sort,
    ...body
}: Omit<
    GetTransactionsParams,
    "page" | "skipCount"
>): Promise<GetTransactionsSnapshotResponse> {
    const response = await transactionContractClient.queryTransactionsSnapshot({
        body: {
            ...body,
            sort: sort ? { field: sort.field, order: sort.order } : undefined,
        },
    });

    if (response.status !== 200) {
        throw new Error("Failed to fetch transactions");
    }

    return getTransactionsSnapshotResponseSchema.parse(response.body);
}

export async function getTransactionsStatistics(
    filters: TransactionFilters,
    workspaceId: string,
): Promise<TransactionsStatistics> {
    const response =
        await transactionContractClient.queryTransactionsStatistics({
            body: {
                filters,
                workspaceId,
            },
        });

    if (response.status !== 200) {
        throw new Error("Failed to fetch transaction statistics");
    }

    return response.body;
}

export async function getSuggestedMatches(data: {
    transactionId: number;
    sort?: TransactionSort;
    filters?: TransactionFilters;
}): Promise<GetTransactionsResponseWithCount> {
    const { transactionId, sort, filters } = data;

    const response = await transactionContractClient.getSuggestedMatches({
        params: {
            id: transactionId,
        },
        query: {
            sort,
            filters,
        },
    });

    if (response.status !== 200) {
        throw new Error("Failed to fetch suggested matches");
    }

    return getTransactionsResponseWithCountSchema.parse(response.body);
}

export async function getSuggestedAccrualMatchTransactions(data: {
    transactionId: number;
    sort?: TransactionSort;
    filters?: TransactionFilters;
}): Promise<GetTransactionsResponseWithCount> {
    const response =
        await transactionContractClient.getSuggestedAccrualMatchTransactions({
            params: {
                id: data.transactionId,
            },
            query: {
                sort: data.sort,
                filters: data.filters,
            },
        });

    if (response.status !== 200) {
        throw new Error("Failed to fetch suggested accrual match transactions");
    }

    return getTransactionsResponseWithCountSchema.parse(response.body);
}

export async function getSuggestedAccrualMatchInvoices(data: {
    transactionId: number;
    query?: SuggestedAccrualMatchInvoicesQuery;
}): Promise<SuggestedAccrualMatchInvoicesResponse> {
    const response =
        await transactionContractClient.getSuggestedAccrualMatchInvoices({
            params: {
                id: data.transactionId,
            },
            query: data.query ?? {
                page: 0,
                limit: 20,
                scope: "suggested",
            },
        });

    if (response.status !== 200) {
        throw new Error("Failed to fetch suggested accrual match invoices");
    }

    return suggestedAccrualMatchInvoicesResponseSchema.parse(response.body);
}

export async function getSuggestedAccrualMatchBills(data: {
    transactionId: number;
    query?: SuggestedAccrualMatchBillsQuery;
}): Promise<SuggestedAccrualMatchBillsResponse> {
    const response =
        await transactionContractClient.getSuggestedAccrualMatchBills({
            params: {
                id: data.transactionId,
            },
            query: data.query ?? {
                page: 0,
                limit: 20,
                scope: "suggested",
            },
        });

    if (response.status !== 200) {
        throw new Error("Failed to fetch suggested accrual match bills");
    }

    return suggestedAccrualMatchBillsResponseSchema.parse(response.body);
}

export async function bulkUpdateTransactions(
    workspaceId: string,
    payload: BulkUpdateTransactionsRequestInput,
): Promise<{
    transactionsBeforeUpdate: Transaction[];
    updatedTransactions: Transaction[];
    canonicalNormalizedDescriptionId: string | null;
}> {
    const response = await transactionContractClient.bulkUpdateTransactions({
        params: {
            workspaceId,
        },
        body: payload,
    });

    if (response.status >= 300) {
        throw new Error("Failed to bulk update transactions");
    }

    const parsedResponse = bulkUpdateTransactionsResponseSchema.parse(
        response.body,
    );

    return {
        transactionsBeforeUpdate:
            parsedResponse.transactionsBeforeUpdate as unknown as Transaction[],
        updatedTransactions:
            parsedResponse.updatedTransactions as unknown as Transaction[],
        canonicalNormalizedDescriptionId:
            parsedResponse.canonicalNormalizedDescriptionId,
    };
}

export function useUnsplitAllTransactions(
    transactionIds: number[],
    onSuccess?: () => void,
) {
    const queryClient = useQueryClient();
    const { error: toastError } = useToaster();

    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async () => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const result =
                await transactionContractClient.bulkUnsplitTransactions({
                    body: {
                        transactionIds,
                    },
                    params: {
                        workspaceId,
                    },
                });
            if (result.status !== 200) {
                throw new Error(MATCHED_INVOICE_OR_BILL_UNSPLIT_MESSAGE);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["transactions"],
            });
            removeJournalEntryQueries();
            onSuccess?.();
        },
        onError: (error) => {
            if (
                isAxiosError(error) &&
                typeof error.response?.data?.message === "string"
            ) {
                toastError(error.response.data.message);
                return;
            }
            toastError(
                error instanceof Error
                    ? error.message
                    : MATCHED_INVOICE_OR_BILL_UNSPLIT_MESSAGE,
            );
        },
    });
}

export async function addTransactionFieldReviews(
    transactionId: number,
    fields: TransactionReviewField[],
): Promise<TransactionFieldReviewDto[]> {
    const res = await fieldReviewClient.addFieldReviews({
        params: { transactionId },
        body: { fields },
    });
    if (res.status !== 200) {
        throw new Error("Failed to add field reviews");
    }
    return res.body.reviews;
}

export async function revokeTransactionFieldReview(
    transactionId: number,
    field: TransactionReviewField,
): Promise<boolean> {
    const res = await fieldReviewClient.revokeFieldReview({
        params: { transactionId, field },
    });
    if (res.status !== 200) {
        throw new Error("Failed to revoke field review");
    }
    return res.body.deleted;
}

export async function bulkAddFieldReviews(
    body: BulkFieldReviewRequest,
): Promise<number> {
    const res = await fieldReviewClient.bulkAddFieldReviews({
        body,
    });
    if (res.status !== 200) {
        throw new Error("Failed to bulk add field reviews");
    }
    return res.body.reviewsCreated;
}

export async function bulkRevokeFieldReviews(
    body: BulkFieldReviewRequest,
): Promise<number> {
    const res = await fieldReviewClient.bulkRevokeFieldReviews({
        body,
    });
    if (res.status !== 200) {
        throw new Error("Failed to bulk revoke field reviews");
    }
    return res.body.reviewsDeleted;
}
